import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAutoTaggingPlanTypes,
  filterConfigurablePlanTypes,
  getSubTypesForObjectType as getSubTypesForObjectTypeFromList,
  resolveDefaultObjectType,
  type AutoTaggingPlanNamedOption,
  type AutoTaggingPlanTypeOption,
} from "../services/autoTaggingService";
import { fetchAssessmentPlanTypes } from "../services/objectToObjectPlanService";

export interface UseAutoTaggingPlanTypesResult {
  planTypes: AutoTaggingPlanTypeOption[];
  objectTypeOptions: AutoTaggingPlanNamedOption[];
  defaultObjectType: string;
  getSubTypesForObjectType: (objectType: string) => AutoTaggingPlanNamedOption[];
  loading: boolean;
  error: string | null;
}

/** Where Object / Sub-Type options are loaded from. */
export type PlanTypesSource =
  | "auto-tagging"
  /** OTO union of Reference + Impacted object groups. */
  | "assessment-plans"
  /** OTO Reference Items Object column. */
  | "assessment-plans-reference"
  /** OTO Impact Items Object column. */
  | "assessment-plans-impacted";

export type UseAutoTaggingPlanTypesOptions = {
  /** When true, only types with `configurable !== false` are exposed. */
  configurableOnly?: boolean;
  /**
   * `auto-tagging` → `/auto-tagging/plans/types` (Tagging / Enhanced).
   * `assessment-plans*` → object types from `/assessment-plans/configurable-concepts` (OTO).
   */
  source?: PlanTypesSource;
  /**
   * When false, skip the network request (e.g. cascade closed).
   * Default true for filter bars / page-level consumers.
   */
  enabled?: boolean;
  /**
   * Bump to force a refetch (e.g. cascade popover open after a failed load).
   * Identity changes only — value is not sent to the API.
   */
  reloadToken?: number;
};

function fetchPlanTypesForSource(
  source: PlanTypesSource,
): Promise<AutoTaggingPlanTypeOption[]> {
  if (source === "assessment-plans-reference") {
    return fetchAssessmentPlanTypes("reference");
  }
  if (source === "assessment-plans-impacted") {
    return fetchAssessmentPlanTypes("impacted");
  }
  if (source === "assessment-plans") {
    return fetchAssessmentPlanTypes("all");
  }
  return fetchAutoTaggingPlanTypes();
}

export function useAutoTaggingPlanTypes(
  options?: UseAutoTaggingPlanTypesOptions,
): UseAutoTaggingPlanTypesResult {
  const configurableOnly = options?.configurableOnly === true;
  const source: PlanTypesSource = options?.source ?? "auto-tagging";
  const enabled = options?.enabled !== false;
  const reloadToken = options?.reloadToken ?? 0;
  const [allPlanTypes, setAllPlanTypes] = useState<AutoTaggingPlanTypeOption[]>(
    [],
  );
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    if (!enabled) {
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    setError(null);

    fetchPlanTypesForSource(source)
      .then((types) => {
        if (!mounted) return;
        setAllPlanTypes(types);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setAllPlanTypes([]);
        setError(err instanceof Error ? err.message : "Failed to load plan types");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [source, enabled, reloadToken]);

  const planTypes = useMemo(
    () =>
      configurableOnly
        ? filterConfigurablePlanTypes(allPlanTypes)
        : allPlanTypes,
    [allPlanTypes, configurableOnly],
  );

  const objectTypeOptions = useMemo(
    () =>
      planTypes.map((item) => ({
        code: item.code,
        name: item.name,
      })),
    [planTypes],
  );

  const defaultObjectType = useMemo(
    () => resolveDefaultObjectType(planTypes),
    [planTypes],
  );

  const getSubTypesForObjectType = useCallback(
    (objectType: string) => getSubTypesForObjectTypeFromList(planTypes, objectType),
    [planTypes],
  );

  return {
    planTypes,
    objectTypeOptions,
    defaultObjectType,
    getSubTypesForObjectType,
    loading,
    error,
  };
}
